import {
  getDaysRemainingAt as calculateDaysRemaining,
  getTimelineStatusAt as getTimelineStatus,
} from '@/helpers/ssr/dates'

import type {
  FederalGrant,
  FederalGrantNotice,
} from '@/types/federal-grant/FederalGrant'
import type { Status } from '@/types/federal-grant/Status'

import type { BadgeVariant } from '../../common/BadgeV2'
import {
  type NormalizedFile,
  formatCurrency,
  formatDate,
  normalizeDepartmentName,
  normalizeFiles,
  sortNoticesByDateField,
} from '../../common/transforms'

// ============================================================================
// Types
// ============================================================================

export interface TimelineEvent {
  id?: string
  date: string
  label: string
  status: 'past' | 'current' | 'future'
  variant?: 'default' | 'amendment' | 'deadline'
  noticeId?: string
}

export interface NormalizedGrantData {
  // Grant ID
  id: string

  // Header
  title: string
  subtitle: string // Agency name
  opportunityNumber: string | null
  badges: Array<{
    label: string
    variant: BadgeVariant
  }>

  // Key Stats
  stats: {
    agencyName: string | null
    agencyCode: string | null
    closeDate: string | null
    closeDateRaw: string | null
    daysRemaining: number | null
    status: Status
    fundingCategory: string | null
    fundingType: string | null
  }

  // Funding Info
  funding: {
    awardCeiling: number | null
    awardFloor: number | null
    estimatedTotal: number | null
    expectedAwards: string | null
    costSharingRequired: boolean
  }

  // Eligibility
  eligibility: {
    eligibleApplicants: string | null
    additionalInfo: string | null
  }

  // Contact Info
  contact: {
    name: string | null
    email: string | null
    phone: string | null
    description: string | null
  }

  // Timeline
  timeline: TimelineEvent[]

  // Notices
  notices: FederalGrantNotice[]
  latestNotice: FederalGrantNotice

  // Content
  description: string | null
  aiSummary: string | null

  // Files
  files: NormalizedFile[]

  // Classification
  cfdaNumbers: string[]

  // External Links
  externalLink: string | null
}

// ============================================================================
// Domain-Specific Utilities
// ============================================================================

/**
 * Map status to badge variant
 */
function getStatusVariant(status: Status | undefined): BadgeVariant {
  if (!status) return 'neutral'
  if (status === 'Posted') return 'success'
  if (status === 'Forecasted') return 'warning'
  return 'neutral'
}

/**
 * Map opportunity category to badge variant
 */
function getCategoryVariant(category: string | undefined): BadgeVariant {
  if (!category) return 'outline'
  const catLower = category.toLowerCase()
  if (catLower.includes('discretionary')) return 'info'
  if (catLower.includes('mandatory')) return 'default'
  if (catLower.includes('continuation')) return 'neutral'
  return 'outline'
}

/**
 * Parse CFDA numbers from comma-separated string
 */
function parseCfdaNumbers(cfda: string | null | undefined): string[] {
  if (!cfda) return []
  return cfda
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

// ============================================================================
// Main Transform
// ============================================================================

/**
 * Normalize raw federal grant data into a format suitable for DisplayV2 components
 */
export function normalizeGrantData(
  grant: FederalGrant,
  referenceMs: number,
): NormalizedGrantData {
  // Sort notices by post_date descending (newest first)
  const sortedNotices = sortNoticesByDateField(grant.notices, 'post_date')

  const latestNotice = sortedNotices[0]

  // Build badges
  const badges: Array<{ label: string; variant: BadgeVariant }> = []

  // Funding instrument type first (Grant, Cooperative Agreement, etc.)
  if (latestNotice.funding_instrument_type) {
    badges.push({
      label: latestNotice.funding_instrument_type,
      variant: 'info',
    })
  } else {
    // Fallback if no funding_instrument_type
    badges.push({ label: 'Grant', variant: 'info' })
  }

  if (latestNotice.status) {
    badges.push({
      label: latestNotice.status,
      variant: getStatusVariant(latestNotice.status),
    })
  }

  if (latestNotice.opportunity_category) {
    badges.push({
      label: latestNotice.opportunity_category,
      variant: getCategoryVariant(latestNotice.opportunity_category),
    })
  }

  // Build timeline
  const timeline: TimelineEvent[] = []

  // Add all notice postings to timeline
  sortedNotices.forEach((notice, idx) => {
    const isOriginal = idx === sortedNotices.length - 1
    const noticeNumber = sortedNotices.length - idx

    let label: string
    if (isOriginal) {
      label = 'Original Notice'
    } else if (notice.opportunity_category) {
      label = notice.opportunity_category
    } else {
      label = `Notice ${noticeNumber}`
    }

    // Append version if available
    if (notice.version && notice.version !== '0') {
      // Only add "v" prefix if version is purely numeric (ignoring whitespace)
      const isNumericVersion = /^\s*\d+\s*$/.test(notice.version)
      const versionPrefix = isNumericVersion ? 'v' : ''
      label = `${label} (${versionPrefix}${notice.version})`
    }

    timeline.push({
      id: `notice-${notice.id}`,
      date: notice.post_date,
      label,
      status: getTimelineStatus(notice.post_date, referenceMs),
      variant: isOriginal ? 'default' : 'amendment',
      noticeId: notice.id,
    })
  })

  // Add close date (deadline)
  if (latestNotice.close_date) {
    timeline.push({
      id: 'close-date',
      date: latestNotice.close_date,
      label: 'Application Deadline',
      status: getTimelineStatus(latestNotice.close_date, referenceMs, true),
      variant: 'deadline',
    })
  }

  // Add archive date
  if (latestNotice.archive_date) {
    timeline.push({
      id: 'archive-date',
      date: latestNotice.archive_date,
      label: 'Archive Date',
      status: getTimelineStatus(latestNotice.archive_date, referenceMs),
      variant: 'default',
    })
  }

  // Sort timeline chronologically
  timeline.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Calculate days remaining until close
  const daysRemaining = calculateDaysRemaining(
    latestNotice.close_date,
    referenceMs,
  )

  // Parse funding amounts
  const awardCeiling = latestNotice.award_ceiling
    ? parseFloat(latestNotice.award_ceiling)
    : null
  const awardFloor = latestNotice.award_floor
    ? parseFloat(latestNotice.award_floor)
    : null
  const estimatedTotal = latestNotice.estimated_total_program_funding
    ? parseFloat(latestNotice.estimated_total_program_funding)
    : null

  // Normalize files
  const files = normalizeFiles(grant.files)

  return {
    id: grant.id,

    title: latestNotice.opportunity_title || 'Federal Grant Opportunity',
    subtitle:
      normalizeDepartmentName(latestNotice.agency_name) || 'Federal Agency',
    opportunityNumber: latestNotice.opportunity_number || null,
    badges,

    stats: {
      agencyName: normalizeDepartmentName(latestNotice.agency_name),
      agencyCode: latestNotice.agency_code || null,
      closeDate: formatDate(latestNotice.close_date),
      closeDateRaw: latestNotice.close_date || null,
      daysRemaining,
      status: latestNotice.status,
      fundingCategory: latestNotice.category_of_funding_activity || null,
      fundingType: latestNotice.funding_instrument_type || null,
    },

    funding: {
      awardCeiling,
      awardFloor,
      estimatedTotal,
      expectedAwards: latestNotice.expected_number_of_awards || null,
      costSharingRequired:
        latestNotice.cost_sharing_or_matching_requirement === 'Yes',
    },

    eligibility: {
      eligibleApplicants: latestNotice.eligible_applicants || null,
      additionalInfo:
        latestNotice.additional_information_on_eligibility || null,
    },

    contact: {
      name: latestNotice.grantor_contact_name || null,
      email: latestNotice.grantor_contact_email || null,
      phone: latestNotice.grantor_contact_phone_number || null,
      description: latestNotice.grantor_contact_text || null,
    },

    timeline,
    notices: sortedNotices,
    latestNotice,

    description: latestNotice.description || null,
    aiSummary: grant.ai_summary || null,

    files,

    cfdaNumbers: parseCfdaNumbers(latestNotice.cfda_numbers),

    externalLink: latestNotice.link || null,
  }
}
